<script setup>
import { ref, watch } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';

const props = defineProps({
  campaign: { type: Object, required: true },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const failures = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);

const fetchFailures = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappCampaignsAPI.getDeliveries(
      props.campaign.id,
      { page, per_page: 25, status: 'failed' }
    );
    failures.value = data.deliveries || [];
    currentPage.value = data.meta?.current_page || 1;
    totalPages.value = data.meta?.total_pages || 0;
    totalCount.value = data.meta?.total_count || 0;
  } catch {
    failures.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.isOpen,
  open => {
    if (open) fetchFailures(1);
  },
  { immediate: true }
);

const goToPage = page => fetchFailures(page);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black6"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl border border-n-weak w-full max-w-2xl max-h-[80vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-n-weak"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              Falhas no envio
            </h3>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ totalCount }} contatos com falha na campanha
              "{{ campaign.title }}"
            </p>
          </div>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="emit('close')"
          >
            <span class="i-lucide-x text-lg" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-4">
          <div
            v-if="isLoading"
            class="flex items-center justify-center py-10"
          >
            <Spinner />
          </div>

          <div v-else-if="failures.length === 0" class="text-center py-10">
            <p class="text-sm text-n-slate-11">
              Nenhuma falha encontrada
            </p>
          </div>

          <div v-else class="flex flex-col gap-2">
            <div
              v-for="failure in failures"
              :key="failure.id"
              class="flex items-start gap-3 p-3 rounded-lg bg-n-alpha-black2 border border-n-weak"
            >
              <span
                class="i-lucide-x-circle text-n-ruby-11 shrink-0 mt-0.5"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ failure.contact_name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ failure.formatted_phone || failure.phone_number }}
                  </span>
                </div>
                <p class="text-xs text-n-ruby-11 mt-1">
                  {{ failure.error_message || 'Erro desconhecido' }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ new Date(failure.created_at).toLocaleString('pt-BR') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-center gap-2 p-3 border-t border-n-weak"
        >
          <button
            class="px-3 py-1 text-xs rounded-md"
            :class="
              currentPage > 1
                ? 'text-n-blue-11 hover:bg-n-blue-3 cursor-pointer'
                : 'text-n-slate-9 cursor-not-allowed'
            "
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >
            Anterior
          </button>
          <span class="text-xs text-n-slate-11">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            class="px-3 py-1 text-xs rounded-md"
            :class="
              currentPage < totalPages
                ? 'text-n-blue-11 hover:bg-n-blue-3 cursor-pointer'
                : 'text-n-slate-9 cursor-not-allowed'
            "
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
